/**
 * IndexedDB wrapper for offline data storage
 * Provides methods to store, retrieve, and sync offline data
 */

import { logger } from '@/utils/logger';

const DB_NAME = 'EnglishAssessmentDB';
const DB_VERSION = 1;
const PENDING_MUTATIONS_STORE = 'pendingMutations';
const CACHED_DATA_STORE = 'cachedData';

interface PendingMutation {
  id: string;
  type: 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  url: string;
  data?: any;
  timestamp: number;
  retryCount: number;
}

interface CachedData {
  key: string;
  data: any;
  timestamp: number;
  expiresAt?: number;
}

/**
 * Initialize IndexedDB database
 */
function openDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve(request.result);

    request.onupgradeneeded = (event) => {
      const db = (event.target as IDBOpenDBRequest).result;

      // Create object stores if they don't exist
      if (!db.objectStoreNames.contains(PENDING_MUTATIONS_STORE)) {
        const mutationStore = db.createObjectStore(PENDING_MUTATIONS_STORE, {
          keyPath: 'id',
          autoIncrement: false
        });
        mutationStore.createIndex('timestamp', 'timestamp', { unique: false });
        mutationStore.createIndex('url', 'url', { unique: false });
      }

      if (!db.objectStoreNames.contains(CACHED_DATA_STORE)) {
        const cacheStore = db.createObjectStore(CACHED_DATA_STORE, {
          keyPath: 'key'
        });
        cacheStore.createIndex('timestamp', 'timestamp', { unique: false });
        cacheStore.createIndex('expiresAt', 'expiresAt', { unique: false });
      }
    };
  });
}

/**
 * Store a pending mutation (failed request) for later sync
 */
export async function storePendingMutation(
  type: PendingMutation['type'],
  url: string,
  data?: any
): Promise<string> {
  const db = await openDB();
  const id = `${type}-${url}-${Date.now()}`;

  const mutation: PendingMutation = {
    id,
    type,
    url,
    data,
    timestamp: Date.now(),
    retryCount: 0
  };

  return new Promise((resolve, reject) => {
    const transaction = db.transaction([PENDING_MUTATIONS_STORE], 'readwrite');
    const store = transaction.objectStore(PENDING_MUTATIONS_STORE);
    const request = store.add(mutation);

    request.onsuccess = () => resolve(id);
    request.onerror = () => reject(request.error);
  });
}

/**
 * Get all pending mutations
 */
export async function getPendingMutations(): Promise<PendingMutation[]> {
  const db = await openDB();

  return new Promise((resolve, reject) => {
    const transaction = db.transaction([PENDING_MUTATIONS_STORE], 'readonly');
    const store = transaction.objectStore(PENDING_MUTATIONS_STORE);
    const request = store.getAll();

    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

/**
 * Delete a pending mutation after successful sync
 */
export async function deletePendingMutation(id: string): Promise<void> {
  const db = await openDB();

  return new Promise((resolve, reject) => {
    const transaction = db.transaction([PENDING_MUTATIONS_STORE], 'readwrite');
    const store = transaction.objectStore(PENDING_MUTATIONS_STORE);
    const request = store.delete(id);

    request.onsuccess = () => resolve();
    request.onerror = () => reject(request.error);
  });
}

/**
 * Update retry count for a pending mutation
 */
export async function updateMutationRetryCount(id: string): Promise<void> {
  const db = await openDB();

  return new Promise((resolve, reject) => {
    const transaction = db.transaction([PENDING_MUTATIONS_STORE], 'readwrite');
    const store = transaction.objectStore(PENDING_MUTATIONS_STORE);
    const getRequest = store.get(id);

    getRequest.onsuccess = () => {
      const mutation = getRequest.result;
      if (mutation) {
        mutation.retryCount += 1;
        const updateRequest = store.put(mutation);

        updateRequest.onsuccess = () => resolve();
        updateRequest.onerror = () => reject(updateRequest.error);
      } else {
        reject(new Error('Mutation not found'));
      }
    };

    getRequest.onerror = () => reject(getRequest.error);
  });
}

/**
 * Store data in cache
 */
export async function cacheData(
  key: string,
  data: any,
  ttl?: number // Time to live in milliseconds
): Promise<void> {
  const db = await openDB();

  const cachedData: CachedData = {
    key,
    data,
    timestamp: Date.now(),
    expiresAt: ttl ? Date.now() + ttl : undefined
  };

  return new Promise((resolve, reject) => {
    const transaction = db.transaction([CACHED_DATA_STORE], 'readwrite');
    const store = transaction.objectStore(CACHED_DATA_STORE);
    const request = store.put(cachedData);

    request.onsuccess = () => resolve();
    request.onerror = () => reject(request.error);
  });
}

/**
 * Get data from cache
 */
export async function getCachedData(key: string): Promise<any | null> {
  const db = await openDB();

  return new Promise((resolve, reject) => {
    const transaction = db.transaction([CACHED_DATA_STORE], 'readonly');
    const store = transaction.objectStore(CACHED_DATA_STORE);
    const request = store.get(key);

    request.onsuccess = () => {
      const result = request.result as CachedData | undefined;

      if (!result) {
        resolve(null);
        return;
      }

      // Check if data has expired
      if (result.expiresAt && result.expiresAt < Date.now()) {
        // Delete expired data
        deleteCachedData(key);
        resolve(null);
        return;
      }

      resolve(result.data);
    };

    request.onerror = () => reject(request.error);
  });
}

/**
 * Delete cached data
 */
export async function deleteCachedData(key: string): Promise<void> {
  const db = await openDB();

  return new Promise((resolve, reject) => {
    const transaction = db.transaction([CACHED_DATA_STORE], 'readwrite');
    const store = transaction.objectStore(CACHED_DATA_STORE);
    const request = store.delete(key);

    request.onsuccess = () => resolve();
    request.onerror = () => reject(request.error);
  });
}

/**
 * Clear all expired cached data
 */
export async function clearExpiredCache(): Promise<void> {
  const db = await openDB();

  return new Promise((resolve, reject) => {
    const transaction = db.transaction([CACHED_DATA_STORE], 'readwrite');
    const store = transaction.objectStore(CACHED_DATA_STORE);
    const index = store.index('expiresAt');
    const request = index.openCursor();

    request.onsuccess = (event) => {
      const cursor = (event.target as IDBRequest).result;
      if (cursor) {
        const cachedData = cursor.value as CachedData;
        if (cachedData.expiresAt && cachedData.expiresAt < Date.now()) {
          cursor.delete();
        }
        cursor.continue();
      } else {
        resolve();
      }
    };

    request.onerror = () => reject(request.error);
  });
}

/**
 * Clear all data from the database
 */
export async function clearAllData(): Promise<void> {
  const db = await openDB();

  return new Promise((resolve, reject) => {
    const transaction = db.transaction(
      [PENDING_MUTATIONS_STORE, CACHED_DATA_STORE],
      'readwrite'
    );

    const mutationStore = transaction.objectStore(PENDING_MUTATIONS_STORE);
    const cacheStore = transaction.objectStore(CACHED_DATA_STORE);

    mutationStore.clear();
    cacheStore.clear();

    transaction.oncomplete = () => resolve();
    transaction.onerror = () => reject(transaction.error);
  });
}

/**
 * Sync pending mutations when back online
 */
export async function syncPendingMutations(
  axiosInstance: any
): Promise<{ success: number; failed: number }> {
  const mutations = await getPendingMutations();
  let success = 0;
  let failed = 0;

  for (const mutation of mutations) {
    try {
      // Attempt to replay the mutation
      await axiosInstance({
        method: mutation.type,
        url: mutation.url,
        data: mutation.data
      });

      // If successful, remove from pending
      await deletePendingMutation(mutation.id);
      success++;
    } catch (error) {
      // Update retry count
      await updateMutationRetryCount(mutation.id);
      failed++;

      // If retry count exceeds threshold, you might want to delete it
      if (mutation.retryCount >= 5) {
        logger.error('Max retries reached for mutation:', mutation);
        await deletePendingMutation(mutation.id);
      }
    }
  }

  return { success, failed };
}
